import type {
  CreatePurchasePayload,
  PurchaseItemPayload,
  ZakPurchaseDto,
  ZakPurchaseItemDto,
} from "../../../api/zakooperApi";
import type { GsItemDto, GsRowDto, GsRowInputItem, GsSettings } from "../../../api/groupSearchApi";
import { nonEmpty } from "../nonEmpty";
import { GS_INITIAL_DELIVERY_TERM_KEY, readGsDeliveryTerm } from "./gsDeliveryTerm";

/**
 * Копия подбора «один в один» для повторного запуска (CAB-677).
 *
 * Результаты проценки бэк хранит ограниченное время, а перезапустить прежнюю
 * закупку с удалённым групповым поиском он не даёт (пробовали: `/start` после
 * PUT отвечает тем же «Указанный id не найден»). Поэтому «Повторить подбор» —
 * это новая сущность с теми же настройками и позициями, запущенная сразу.
 * Здесь — чистые преобразования DTO прежнего подбора в тела запросов создания.
 */

const DEFAULT_PURCHASE_TITLE = "Закупка";

/** Дата в формате бэка `YYYY-MM-DD` уже прошла относительно `today`. */
function isPastDate(value: string | null, today: Date): boolean {
  if (!value) return false;
  const parsed = new Date(`${value.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return false;
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return parsed < startOfToday;
}

/**
 * Тело `POST /purchases` из прежней закупки. Переносим всё, что задавалось при
 * создании; крайний срок — только если он ещё не прошёл: прошедший дедлайн у новой
 * закупки лишён смысла и ломал бы режим «ближе к крайнему сроку».
 */
export function purchaseClonePayload(source: ZakPurchaseDto, today: Date = new Date()): CreatePurchasePayload {
  return {
    title: nonEmpty(source.title.trim()) ?? DEFAULT_PURCHASE_TITLE,
    doc_osn_uuid: source.doc_osn_uuid,
    doc_osn_title: source.doc_osn_title,
    is_autoorder: source.is_autoorder,
    plugins: source.plugins.map((plugin) => ({ ...plugin })),
    brands_trusted: source.brands_trusted,
    purchase_deadline: isPastDate(source.purchase_deadline, today) ? null : source.purchase_deadline,
    extra_days: source.extra_days,
    order_type: source.order_type,
    actualization_period: source.actualization_period,
    price_diff_percent: source.price_diff_percent,
    items_filter_rules: source.items_filter_rules,
    algos: source.algos,
    add_info: source.add_info,
    comment: source.comment,
    trigger_settings: source.trigger_settings,
  };
}

/** Позиция прежней закупки → позиция новой (без серверных полей и итогов прошлой проценки). */
export function purchaseItemClonePayload(item: ZakPurchaseItemDto): PurchaseItemPayload {
  const price = typeof item.price === "number" ? item.price : Number.parseFloat(item.price);
  return {
    article: item.article,
    brand: item.brand,
    name: item.name ?? "",
    ...(Number.isFinite(price) && price > 0 ? { price } : {}),
    qty: item.qty,
    is_no_replace: item.is_no_replace,
    is_important: item.is_important,
    is_autoorder: item.is_autoorder,
    brands_trusted: item.brands_trusted,
    comment: item.comment,
  };
}

/**
 * Настройки нового группового поиска из прежнего (ручной режим и аналитика).
 * Внешние идентификаторы (`ext_*`) принадлежат исходной сущности — в копию не идут.
 */
export function groupSearchClonePayload(source: GsItemDto): GsSettings {
  const settings: GsSettings = { ...source.settings, plugins: [...source.settings.plugins] };
  delete settings.ext_type;
  delete settings.ext_uuid;
  delete settings.ext_type_meta;
  delete settings.ext_name;
  if (settings.mode === "manual") {
    settings.analyze = { ...settings.analyze };
    delete settings.analyze.max_time;
    settings.add_info = {
      ...settings.add_info,
      [GS_INITIAL_DELIVERY_TERM_KEY]: readGsDeliveryTerm(source.settings),
    };
  }
  return settings;
}

/** Строка прежнего группового поиска → строка нового (только входные поля). */
export function gsRowClonePayload(row: GsRowDto): GsRowInputItem {
  const payload: GsRowInputItem = { article: row.article, brand: row.brand };
  if (row.comment) payload.comment = row.comment;
  if (row.settings) payload.settings = { ...row.settings };
  if (row.add_info) payload.add_info = { ...row.add_info };
  return payload;
}
